import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, Loader2, MessageCircle, User, Megaphone } from "lucide-react";
import { toast, Toaster } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

function formatTime(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay
      ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
      : d.toLocaleDateString([], { month: "short", day: "numeric" }) + " · " +
        d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

function MessageBubble({ msg, mineRole }) {
  const isMine = msg.sender_role === mineRole;
  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`msg-${msg.id}`}>
      <div className={`max-w-[80%] sm:max-w-[70%] px-4 py-3 rounded-2xl ${
        isMine
          ? "bg-gradient-to-br from-[#0055FF] to-[#00A8FF] text-white rounded-br-sm"
          : "bg-white/8 border border-white/10 text-white/90 rounded-bl-sm"
      }`}>
        <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">{msg.body}</div>
        <div className={`mt-1 text-[10px] uppercase tracking-[0.15em] ${isMine ? "text-white/70" : "text-white/40"}`}>
          {formatTime(msg.created_at)}
        </div>
      </div>
    </div>
  );
}

function ChatThread({ messages, mineRole, onSend, sending, headerLeft, headerRight, emptyHint }) {
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const submit = async (e) => {
    e.preventDefault();
    const v = text.trim();
    if (!v) return;
    const ok = await onSend(v);
    if (ok) setText("");
  };

  return (
    <div className="card-dark flex flex-col h-[70vh] min-h-[460px]" data-testid="chat-thread">
      <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">{headerLeft}</div>
        {headerRight}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-6 space-y-3" data-testid="chat-messages">
        {messages.length === 0 ? (
          <div className="h-full grid place-items-center text-center text-white/50">
            <div>
              <MessageCircle className="mx-auto mb-3 text-[#00E5FF]" />
              <div className="text-sm">{emptyHint}</div>
            </div>
          </div>
        ) : messages.map((m) => <MessageBubble key={m.id} msg={m} mineRole={mineRole} />)}
      </div>

      <form onSubmit={submit} className="border-t border-white/10 p-3 flex items-end gap-2" data-testid="chat-input-form">
        <textarea
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(e); }
          }}
          placeholder="Type a message…  (Shift+Enter for newline)"
          className="flex-1 bg-transparent border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:border-[#00E5FF]/60 outline-none resize-none max-h-40"
          data-testid="chat-input"
        />
        <button type="submit" disabled={sending || !text.trim()} className="btn-electric !px-5 !py-3" data-testid="chat-send-btn">
          {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
        </button>
      </form>
    </div>
  );
}

function ParentMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/me");
      setMessages(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
    const t = setInterval(load, 8000); // light polling
    return () => clearInterval(t);
  }, [load]);

  const onSend = async (body) => {
    setSending(true);
    try {
      const { data } = await api.post("/messages/me", { body });
      setMessages((prev) => [...prev, data]);
      return true;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Send failed");
      return false;
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="overline mb-3">Coach Direct Line</div>
      <h1 className="font-display uppercase text-4xl sm:text-5xl text-chrome">Message Coach</h1>
      <p className="mt-3 text-white/65 max-w-xl">
        Got a question about practice, uniforms, or your dancer? Drop Coach a note here — replies appear in this thread.
      </p>

      <div className="mt-8">
        {loading ? (
          <div className="card-dark h-[70vh] grid place-items-center text-white/40">
            <Loader2 className="animate-spin" />
          </div>
        ) : (
          <ChatThread
            messages={messages}
            mineRole="parent"
            sending={sending}
            onSend={onSend}
            emptyHint="No messages yet — say hi!"
            headerLeft={
              <>
                <div className="h-10 w-10 rounded-full grid place-items-center bg-gradient-to-br from-[#0055FF] to-[#00E5FF] text-white">
                  <User size={16} />
                </div>
                <div className="min-w-0">
                  <div className="font-varsity tracking-wider text-lg">Coach Admin</div>
                  <div className="text-xs text-white/50">Dazzlin&apos; Dymes Dance Team</div>
                </div>
              </>
            }
          />
        )}
      </div>
    </div>
  );
}

function BroadcastDialog({ open, onOpenChange, parentCount, onSent }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const v = body.trim();
    if (!v) return;
    if (!window.confirm(`Send this to all ${parentCount} parent${parentCount === 1 ? "" : "s"}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages/broadcast", { body: v });
      const sentTxt = `Sent to ${data.sent} parent${data.sent === 1 ? "" : "s"}`;
      const pushTxt = data.push_devices > 0
        ? `📣 ${sentTxt} · pinging ${data.push_devices} device${data.push_devices === 1 ? "" : "s"}`
        : `${sentTxt} · no devices have notifications on yet`;
      toast.success(pushTxt);
      setBody("");
      onOpenChange(false);
      onSent?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0E] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-chrome inline-flex items-center gap-2">
            <Megaphone size={20} className="text-[#00E5FF]" /> Broadcast to All Parents
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="text-xs text-white/55">
            This message will land in every parent&apos;s direct thread with you, so each family gets it privately.
          </div>
          <textarea
            rows={6}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Hi families! Quick reminder: practice this Saturday is at 10am sharp. Bring water and the new black warm-up tops!"
            className="w-full bg-white/5 border border-white/10 focus:border-[#00E5FF]/60 outline-none rounded-md px-3 py-3 text-white placeholder-white/30 resize-none leading-relaxed"
            data-testid="broadcast-input"
            required
          />
          <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
            <div className="text-[11px] text-white/45">{parentCount} recipient{parentCount === 1 ? "" : "s"}</div>
            <button type="submit" disabled={sending || !body.trim()} className="btn-electric" data-testid="broadcast-send-btn">
              {sending ? <Loader2 className="animate-spin" size={14} /> : <><Megaphone size={14} /> Send to All</>}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function AdminMessages() {
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null); // { parent, messages }
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [params, setParams] = useSearchParams();

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data);
      // Auto-select first thread on first load if none picked yet
      const fromQuery = params.get("parent");
      if (!activeId && data.length > 0) {
        setActiveId(fromQuery && data.find((t) => t.parent_id === fromQuery) ? fromQuery : data[0].parent_id);
      }
    } catch { /* ignore */ }
  }, [activeId, params]);

  const loadThread = useCallback(async (pid) => {
    if (!pid) return;
    setLoadingThread(true);
    try {
      const { data } = await api.get(`/messages/threads/${pid}`);
      setActive(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setLoadingThread(false);
    }
  }, []);

  useEffect(() => {
    setLoadingList(true);
    loadThreads().finally(() => setLoadingList(false));
    const t = setInterval(loadThreads, 10000);
    return () => clearInterval(t);
  }, [loadThreads]);

  useEffect(() => {
    if (activeId) {
      loadThread(activeId);
      setParams((p) => { p.set("parent", activeId); return p; }, { replace: true });
    }
  }, [activeId, loadThread, setParams]);

  const onSend = async (body) => {
    if (!activeId) return false;
    setSending(true);
    try {
      const { data } = await api.post(`/messages/threads/${activeId}`, { body });
      setActive((prev) => prev ? { ...prev, messages: [...prev.messages, data] } : prev);
      if (typeof data.push_devices === "number") {
        if (data.push_devices > 0) {
          toast.success(`Sent · pinging ${data.push_devices} device${data.push_devices === 1 ? "" : "s"}`);
        } else {
          toast.success("Sent · parent hasn't enabled notifications yet");
        }
      }
      // Refresh sidebar last-message preview
      loadThreads();
      return true;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Send failed");
      return false;
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="overline mb-3">Coach Inbox</div>
      <h1 className="font-display uppercase text-4xl sm:text-5xl text-chrome">Parent Messages</h1>
      <p className="mt-3 text-white/65 max-w-xl">Direct 1-on-1 thread with every parent. Unread families bubble to the top.</p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          onClick={() => setBroadcastOpen(true)}
          disabled={threads.length === 0}
          className="btn-electric !px-4 !py-2 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="open-broadcast-btn"
        >
          <Megaphone size={14} /> Broadcast to All Parents
        </button>
        <span className="text-xs text-white/45">Sends the same message to every parent&apos;s thread at once.</span>
      </div>

      <BroadcastDialog
        open={broadcastOpen}
        onOpenChange={setBroadcastOpen}
        parentCount={threads.length}
        onSent={() => { loadThreads(); if (activeId) loadThread(activeId); }}
      />

      <div className="mt-8 grid md:grid-cols-[320px_1fr] gap-4">
        {/* Sidebar */}
        <aside className="card-dark p-2 max-h-[70vh] overflow-y-auto" data-testid="admin-thread-list">
          {loadingList ? (
            <div className="p-6 text-white/40 text-sm">Loading…</div>
          ) : threads.length === 0 ? (
            <div className="p-6 text-white/40 text-sm">No parents have registered yet.</div>
          ) : (
            <ul className="space-y-1">
              {threads.map((t) => {
                const isActive = t.parent_id === activeId;
                return (
                  <li key={t.parent_id}>
                    <button
                      onClick={() => setActiveId(t.parent_id)}
                      className={`w-full text-left px-3 py-3 rounded-lg transition-all ${
                        isActive ? "bg-white/10 border border-[#00E5FF]/40" : "border border-transparent hover:bg-white/5"
                      }`}
                      data-testid={`thread-item-${t.parent_id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-varsity tracking-wider truncate">{t.parent_name || "Parent"}</div>
                          <div className="text-[11px] text-white/45 truncate">
                            {t.dancer_name ? `Dancer: ${t.dancer_name}` : t.parent_email}
                          </div>
                          {t.last_message && (
                            <div className="text-xs text-white/55 mt-1 line-clamp-2">
                              {t.last_message.sender_role === "admin" ? "You: " : ""}{t.last_message.body}
                            </div>
                          )}
                        </div>
                        {t.unread_count > 0 && (
                          <span className="shrink-0 min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-[#00E5FF] text-black text-[10px] font-bold" data-testid={`unread-badge-${t.parent_id}`}>
                            {t.unread_count}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* Thread pane */}
        <div>
          {!activeId ? (
            <div className="card-dark h-[70vh] grid place-items-center text-white/50">Pick a parent to start chatting</div>
          ) : loadingThread || !active ? (
            <div className="card-dark h-[70vh] grid place-items-center text-white/40"><Loader2 className="animate-spin" /></div>
          ) : (
            <ChatThread
              messages={active.messages}
              mineRole="admin"
              sending={sending}
              onSend={onSend}
              emptyHint="No messages with this parent yet. Send the first one!"
              headerLeft={
                <>
                  <div className="h-10 w-10 rounded-full grid place-items-center bg-gradient-to-br from-[#0055FF] to-[#00E5FF] text-white">
                    <User size={16} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-varsity tracking-wider text-lg truncate">{active.parent.name}</div>
                    <div className="text-xs text-white/50 truncate">
                      {active.parent.email}{active.parent.dancer_name ? ` · Dancer: ${active.parent.dancer_name}` : ""}
                    </div>
                  </div>
                </>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const isAdmin = user && user !== false && user.role === "admin";

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <Toaster theme="dark" position="top-right" richColors />
      <main className="mx-auto max-w-7xl w-full px-6 py-12 flex-1">
        {isAdmin ? <AdminMessages /> : <ParentMessages />}
      </main>
      <Footer />
    </div>
  );
}
